import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AIBackgroundEffects from "./AIBackgroundEffects";
import API_CONFIG, { buildApiUrl } from "../config/api.config";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filters, setFilters] = useState({
    status: "all",
    search: "",
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: 39,
    paymentMethod: "manual",
    transactionId: "",
    notes: "",
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }

    // Set axios default header
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    fetchDashboardData();
  }, [navigate]);

  useEffect(() => {
    fetchSubscriptions();
  }, [filters]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(
        buildApiUrl(API_CONFIG.ENDPOINTS.ADMIN_STATS)
      );
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      });

      const response = await axios.get(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.ADMIN_SUBSCRIPTIONS}?${params}`)
      );

      setSubscriptions(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (subscriptionId, newStatus) => {
    try {
      await axios.put(
        buildApiUrl(
          API_CONFIG.ENDPOINTS.ADMIN_SUBSCRIPTION_STATUS(subscriptionId)
        ),
        { status: newStatus }
      );

      // Refresh data
      fetchSubscriptions();
      fetchDashboardData();
      setSelectedSubscription(null);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error al actualizar el estado");
    }
  };

  const handleDeleteSubscription = async () => {
    if (!selectedSubscription) return;

    setDeleteLoading(true);
    try {
      await axios.delete(
        buildApiUrl(
          API_CONFIG.ENDPOINTS.ADMIN_DELETE_SUBSCRIPTION(
            selectedSubscription._id
          )
        )
      );

      // Show success notification
      setNotification({
        type: "success",
        message:
          "La suscripción y todos los recursos asociados se han eliminado exitosamente",
      });

      // Auto-hide notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);

      // Refresh data
      fetchSubscriptions();
      fetchDashboardData();
      setSelectedSubscription(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting subscription:", error);
      setNotification({
        type: "error",
        message:
          error.response?.data?.message || "Error al eliminar la suscripción",
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFileDownload = async (subscriptionId, fileIndex) => {
    try {
      const response = await axios.get(
        buildApiUrl(
          API_CONFIG.ENDPOINTS.DOWNLOAD_FILE(subscriptionId, fileIndex)
        ),
        {
          responseType: "blob",
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "download";
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          filename = fileNameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Error al descargar el archivo");
    }
  };

  const handleRegisterPayment = async () => {
    if (!selectedSubscription) return;

    setPaymentLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.post(
        buildApiUrl("/api/admin/payments"),
        {
          subscriptionId: selectedSubscription._id,
          amount: paymentForm.amount,
          paymentMethod: paymentForm.paymentMethod,
          transactionId: paymentForm.transactionId,
          notes: paymentForm.notes,
          status: "paid",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Actualizar estado de suscripción a activo si está pendiente
      if (selectedSubscription.subscriptionStatus === "pending") {
        await axios.put(
          buildApiUrl(
            `/api/subscriptions/${selectedSubscription._id}/activate`
          ),
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }

      setNotification({
        type: "success",
        message: "Pago registrado exitosamente",
      });

      setTimeout(() => setNotification(null), 5000);

      // Reset form and close modal
      setShowPaymentModal(false);
      setPaymentForm({
        amount: 39,
        paymentMethod: "manual",
        transactionId: "",
        notes: "",
      });
      setSelectedSubscription(null);

      // Refresh data
      fetchSubscriptions();
      fetchDashboardData();
    } catch (error) {
      console.error("Error registrando pago:", error);
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al registrar el pago",
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminInfo");
    delete axios.defaults.headers.common["Authorization"];
    navigate("/admin");
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: "badge-success",
      pending: "badge-warning",
      cancelled: "badge-danger",
      expired: "badge-secondary",
    };
    return `status-badge ${statusClasses[status] || "badge-secondary"}`;
  };

  const getAgentStatusBadge = (status) => {
    const statusClasses = {
      ready: "badge-success",
      active: "badge-success",
      creating: "badge-warning",
      not_created: "badge-secondary",
      failed: "badge-danger",
      suspended: "badge-danger",
    };
    const statusLabels = {
      ready: "🤖 Listo",
      active: "🤖 Activo",
      creating: "⏳ Creando",
      not_created: "⭕ No Creado",
      failed: "❌ Falló",
      suspended: "⚠️ Suspendido",
    };
    return {
      className: `status-badge ${statusClasses[status] || "badge-secondary"}`,
      label: statusLabels[status] || status,
    };
  };

  const getPaymentStatusBadge = (subscriptionStatus) => {
    if (subscriptionStatus === "active") {
      return {
        className: "status-badge badge-success",
        label: "✓ Pagado",
        isPaid: true,
      };
    } else if (subscriptionStatus === "pending") {
      return {
        className: "status-badge badge-warning",
        label: "⏳ Pendiente",
        isPaid: false,
      };
    } else if (subscriptionStatus === "cancelled") {
      return {
        className: "status-badge badge-danger",
        label: "❌ No Pagado",
        isPaid: false,
      };
    }
    return {
      className: "status-badge badge-secondary",
      label: "⭕ N/A",
      isPaid: false,
    };
  };

  return (
    <div className="admin-dashboard">
      <AIBackgroundEffects step={1} withSidebar={true} />
      {/* Notification Toast */}
      {notification && (
        <div className="notification-toast">
          <div
            className={`notification-content ${
              notification.type === "success"
                ? "notification-success"
                : "notification-error"
            }`}
          >
            <div className="notification-icon">
              {notification.type === "success" ? "✅" : "❌"}
            </div>
            <div className="notification-text">
              <div className="notification-title">
                {notification.type === "success" ? "¡Éxito!" : "Error"}
              </div>
              <div className="notification-message">{notification.message}</div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="notification-close"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <header className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Panel de Administración</h1>
            <p>Gestión de Agentes de Voz con IA</p>
          </div>
          <button onClick={handleLogout} className="logout-button">
            Cerrar Sesión
          </button>
        </div>
      </header>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card-dashboard">
            <h3>Suscripciones Totales</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
          <div className="stat-card-dashboard stat-card-success">
            <h3>Activas</h3>
            <p className="stat-value">{stats.active}</p>
          </div>
          <div className="stat-card-dashboard stat-card-warning">
            <h3>Pendientes</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
          <div className="stat-card-dashboard stat-card-info">
            <h3>Ingresos Mensuales</h3>
            <p className="stat-value">${stats.monthlyRevenue}</p>
          </div>
        </div>
      )}

      <div className="subscriptions-section">
        <div className="section-header">
          <h2>Suscripciones</h2>
          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value, page: 1 })
              }
              className="filter-select"
            >
              <option value="all">Todos los Estados</option>
              <option value="active">Activo</option>
              <option value="pending">Pendiente</option>
              <option value="cancelled">Cancelado</option>
              <option value="expired">Expirado</option>
            </select>
            <input
              type="text"
              placeholder="Buscar por nombre, correo..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value, page: 1 })
              }
              className="search-input"
            />
          </div>
        </div>

        <div className="table-container">
          <table className="subscriptions-table">
            <thead>
              <tr>
                <th>Nombre del Negocio</th>
                <th>Contacto</th>
                <th>Correo</th>
                <th>Teléfono</th>
                <th>Estado</th>
                <th>Pago</th>
                <th>Agente</th>
                <th>Registro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    Cargando...
                  </td>
                </tr>
              ) : subscriptions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    No se encontraron suscripciones
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub._id}>
                    <td>
                      <strong>{sub.businessName}</strong>
                      <br />
                      <small className="text-muted">
                        {sub.businessWebsite}
                      </small>
                    </td>
                    <td>{sub.contactName}</td>
                    <td>{sub.email}</td>
                    <td>{sub.phoneNumber}</td>
                    <td>
                      <span className={getStatusBadge(sub.subscriptionStatus)}>
                        {sub.subscriptionStatus}
                      </span>
                    </td>
                    <td>
                      <div className="payment-status-column">
                        <span
                          className={
                            getPaymentStatusBadge(sub.subscriptionStatus)
                              .className
                          }
                        >
                          {getPaymentStatusBadge(sub.subscriptionStatus).label}
                        </span>
                        {!getPaymentStatusBadge(sub.subscriptionStatus)
                          .isPaid && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSubscription(sub);
                              setShowPaymentModal(true);
                              setPaymentForm({
                                amount: sub.subscriptionAmount || 39,
                                paymentMethod: "manual",
                                transactionId: "",
                                notes: `Pago mensual - ${sub.businessName}`,
                              });
                            }}
                            className="payment-button"
                            title="Registrar Pago"
                          >
                            💳 Pagar
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="agent-status-column">
                        <span
                          className={
                            getAgentStatusBadge(
                              sub.agentStatus || "not_created"
                            ).className
                          }
                        >
                          {
                            getAgentStatusBadge(
                              sub.agentStatus || "not_created"
                            ).label
                          }
                        </span>
                        {sub.agentStatus === "ready" &&
                          sub.elevenLabsAgent?.agentId && (
                            <a
                              href={`https://elevenlabs.io/app/talk-to?agent_id=${sub.elevenLabsAgent.agentId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="test-agent-link"
                              onClick={(e) => e.stopPropagation()}
                              title="Probar agente de voz"
                            >
                              🎤 Probar
                            </a>
                          )}
                      </div>
                    </td>
                    <td>{formatDate(sub.registrationDate)}</td>
                    <td>
                      <button
                        onClick={() => setSelectedSubscription(sub)}
                        className="action-button"
                      >
                        Gestionar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              className="pagination-button"
            >
              Anterior
            </button>
            <span className="pagination-info">
              Página {pagination.currentPage} de {pagination.totalPages}
            </span>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page === pagination.totalPages}
              className="pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {selectedSubscription && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedSubscription(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Gestionar Suscripción</h3>
              <button
                onClick={() => setSelectedSubscription(null)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="subscription-details">
                <h4>{selectedSubscription.businessName}</h4>

                {/* Sección: Información de Contacto */}
                <div className="detail-section">
                  <h5 className="section-title">📋 Información de Contacto</h5>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Contacto:</span>
                      <span className="detail-value">
                        {selectedSubscription.contactName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Correo:</span>
                      <span className="detail-value">
                        {selectedSubscription.email}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Teléfono:</span>
                      <span className="detail-value">
                        {selectedSubscription.phoneNumber}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Sitio Web:</span>
                      <a
                        href={selectedSubscription.businessWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-link"
                      >
                        {selectedSubscription.businessWebsite} 🔗
                      </a>
                    </div>
                  </div>
                </div>

                {/* Sección: Detalles de Suscripción */}
                <div className="detail-section">
                  <h5 className="section-title">💳 Detalles de Suscripción</h5>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Estado:</span>
                      <span
                        className={getStatusBadge(
                          selectedSubscription.subscriptionStatus
                        )}
                      >
                        {selectedSubscription.subscriptionStatus}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Monto Mensual:</span>
                      <span className="detail-value detail-amount">
                        ${selectedSubscription.subscriptionAmount || 39}.00 USD
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Fecha de Registro:</span>
                      <span className="detail-value">
                        {formatDate(selectedSubscription.registrationDate)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        Última Actualización:
                      </span>
                      <span className="detail-value">
                        {formatDate(
                          selectedSubscription.updatedAt ||
                            selectedSubscription.registrationDate
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">ID de Suscripción:</span>
                      <span className="detail-value detail-id">
                        {selectedSubscription._id}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sección: Personalización del Bot */}
                {selectedSubscription.botCustomization && (
                  <div className="detail-section">
                    <h5 className="section-title">
                      🎨 Personalización del Bot
                    </h5>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Tipo de Voz:</span>
                        <span className="detail-value">
                          {selectedSubscription.botCustomization.voiceType ===
                          "female"
                            ? "🗣️ Femenina"
                            : "🗣️ Masculina"}
                        </span>
                      </div>
                      {selectedSubscription.botCustomization.firstMessage && (
                        <div className="detail-item full-width">
                          <span className="detail-label">Primer Mensaje:</span>
                          <div className="detail-box">
                            "
                            {selectedSubscription.botCustomization.firstMessage}
                            "
                          </div>
                        </div>
                      )}
                      {selectedSubscription.botCustomization.systemPrompt && (
                        <div className="detail-item full-width">
                          <span className="detail-label">
                            Prompt del Sistema:
                          </span>
                          <div className="detail-box detail-scrollable">
                            {selectedSubscription.botCustomization.systemPrompt}
                          </div>
                        </div>
                      )}
                      {selectedSubscription.botCustomization
                        .knowledgeBaseUrls &&
                        selectedSubscription.botCustomization.knowledgeBaseUrls
                          .length > 0 && (
                          <div className="detail-item full-width">
                            <span className="detail-label">
                              URLs de Base de Conocimiento (
                              {
                                selectedSubscription.botCustomization
                                  .knowledgeBaseUrls.length
                              }
                              ):
                            </span>
                            <div className="kb-urls-list">
                              {selectedSubscription.botCustomization.knowledgeBaseUrls.map(
                                (url, idx) => (
                                  <div key={idx} className="kb-url-item">
                                    <span className="kb-url-number">
                                      {idx + 1}.
                                    </span>
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="kb-url-link"
                                    >
                                      {url} 🔗
                                    </a>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Agent Information */}
                {selectedSubscription.elevenLabsAgent && (
                  <div className="agent-info-section">
                    <h4>🤖 Información del Agente de Voz</h4>
                    <p>
                      <strong>Estado:</strong>
                      <span
                        className={
                          getAgentStatusBadge(
                            selectedSubscription.agentStatus || "not_created"
                          ).className
                        }
                      >
                        {
                          getAgentStatusBadge(
                            selectedSubscription.agentStatus || "not_created"
                          ).label
                        }
                      </span>
                    </p>
                    {selectedSubscription.elevenLabsAgent.agentId && (
                      <>
                        <p>
                          <strong>ID del Agente:</strong>{" "}
                          <code>
                            {selectedSubscription.elevenLabsAgent.agentId}
                          </code>
                        </p>
                        <p>
                          <strong>Nombre del Agente:</strong>{" "}
                          {selectedSubscription.elevenLabsAgent.agentName}
                        </p>
                        <p>
                          <strong>Primer Mensaje:</strong> "
                          {selectedSubscription.elevenLabsAgent.firstMessage}"
                        </p>
                        <p>
                          <strong>Creado:</strong>{" "}
                          {selectedSubscription.elevenLabsAgent.createdAt
                            ? formatDate(
                                selectedSubscription.elevenLabsAgent.createdAt
                              )
                            : "N/A"}
                        </p>

                        {/* Agent Test URL */}
                        <div className="agent-test-url-container">
                          <p className="test-url-label">
                            🎤 URL de Prueba del Agente de Voz:
                          </p>
                          <div className="test-url-box">
                            <input
                              type="text"
                              value={`https://elevenlabs.io/app/talk-to?agent_id=${selectedSubscription.elevenLabsAgent.agentId}`}
                              readOnly
                              onClick={(e) => e.target.select()}
                              className="agent-url-input"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `https://elevenlabs.io/app/talk-to?agent_id=${selectedSubscription.elevenLabsAgent.agentId}`
                                );
                                alert(
                                  "¡URL de prueba copiada al portapapeles!"
                                );
                              }}
                              className="copy-url-button"
                              title="Copiar URL al portapapeles"
                            >
                              📋 Copiar
                            </button>
                            <a
                              href={`https://elevenlabs.io/app/talk-to?agent_id=${selectedSubscription.elevenLabsAgent.agentId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="open-agent-link"
                            >
                              🔗 Abrir
                            </a>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedSubscription.elevenLabsAgent.errorDetails && (
                      <p className="agent-error">
                        <strong>Error:</strong>{" "}
                        {selectedSubscription.elevenLabsAgent.errorDetails}
                      </p>
                    )}
                  </div>
                )}

                {selectedSubscription.additionalNotes && (
                  <>
                    <p>
                      <strong>Notas:</strong>
                    </p>
                    <div className="notes-box">
                      {selectedSubscription.additionalNotes}
                    </div>
                  </>
                )}

                {selectedSubscription.uploadedFiles?.length > 0 && (
                  <>
                    <p>
                      <strong>
                        Archivos Cargados (
                        {selectedSubscription.uploadedFiles.length}):
                      </strong>
                    </p>
                    <div className="uploaded-files-container">
                      {selectedSubscription.uploadedFiles.map((file, index) => (
                        <div
                          key={file._id || index}
                          className="uploaded-file-item"
                        >
                          <div className="file-icon">
                            {file.fileType?.includes("pdf")
                              ? "📄"
                              : file.fileType?.includes("image")
                              ? "🖼️"
                              : file.fileType?.includes("word") ||
                                file.fileType?.includes("document")
                              ? "📝"
                              : file.fileType?.includes("sheet") ||
                                file.fileType?.includes("excel")
                              ? "📊"
                              : "📎"}
                          </div>
                          <div className="file-details">
                            <div className="file-name">
                              {file.originalName ||
                                file.fileName ||
                                "Archivo sin nombre"}
                            </div>
                            <div className="file-meta">
                              <span className="file-type">
                                {file.fileType || "Tipo desconocido"}
                              </span>
                              {file.fileSize && (
                                <>
                                  <span className="file-separator">•</span>
                                  <span className="file-size">
                                    {(file.fileSize / 1024 / 1024).toFixed(2)}{" "}
                                    MB
                                  </span>
                                </>
                              )}
                              {file.uploadDate && (
                                <>
                                  <span className="file-separator">•</span>
                                  <span className="file-date">
                                    {new Date(
                                      file.uploadDate
                                    ).toLocaleDateString()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            className="file-download-button"
                            onClick={() =>
                              handleFileDownload(
                                selectedSubscription._id,
                                index
                              )
                            }
                            title="Download file"
                          >
                            ⬇
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {(!selectedSubscription.uploadedFiles ||
                  selectedSubscription.uploadedFiles.length === 0) && (
                  <p>
                    <strong>Archivos Cargados:</strong> Sin archivos cargados
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <h4>Cambiar Estado:</h4>
                <div className="status-buttons">
                  <button
                    onClick={() =>
                      handleStatusChange(selectedSubscription._id, "active")
                    }
                    className="status-button status-button-success"
                    disabled={
                      selectedSubscription.subscriptionStatus === "active"
                    }
                  >
                    Activar
                  </button>
                  <button
                    onClick={() =>
                      handleStatusChange(selectedSubscription._id, "pending")
                    }
                    className="status-button status-button-warning"
                    disabled={
                      selectedSubscription.subscriptionStatus === "pending"
                    }
                  >
                    Poner Pendiente
                  </button>
                  <button
                    onClick={() =>
                      handleStatusChange(selectedSubscription._id, "cancelled")
                    }
                    className="status-button status-button-danger"
                    disabled={
                      selectedSubscription.subscriptionStatus === "cancelled"
                    }
                  >
                    Cancelar
                  </button>
                </div>

                <div className="danger-zone">
                  <h4 className="danger-zone-title">Zona de Peligro:</h4>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="danger-zone-button"
                  >
                    🗑️ Eliminar Suscripción Permanentemente
                  </button>
                  <small className="danger-zone-description">
                    Esto eliminará permanentemente la suscripción, el agente de
                    ElevenLabs y todos los archivos cargados.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {/* Payment Registration Modal */}
      {showPaymentModal && selectedSubscription && (
        <div
          className="modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="modal-content payment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>💳 Registrar Pago Manual</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-form">
                <p className="payment-info">
                  <strong>Suscripción:</strong>{" "}
                  {selectedSubscription.businessName}
                </p>
                <p className="payment-info">
                  <strong>Email:</strong> {selectedSubscription.email}
                </p>

                <div className="form-group">
                  <label>Monto (USD)</label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        amount: parseFloat(e.target.value),
                      })
                    }
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Método de Pago</label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        paymentMethod: e.target.value,
                      })
                    }
                    className="form-select"
                  >
                    <option value="manual">Manual</option>
                    <option value="bank_transfer">
                      Transferencia Bancaria
                    </option>
                    <option value="cash">Efectivo</option>
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>ID de Transacción (Opcional)</label>
                  <input
                    type="text"
                    value={paymentForm.transactionId}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        transactionId: e.target.value,
                      })
                    }
                    className="form-input"
                    placeholder="TX-12345..."
                  />
                </div>

                <div className="form-group">
                  <label>Notas</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, notes: e.target.value })
                    }
                    className="form-textarea"
                    rows="3"
                    placeholder="Notas adicionales sobre el pago..."
                  />
                </div>

                <div className="payment-actions">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="cancel-button"
                    disabled={paymentLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleRegisterPayment}
                    className="confirm-button"
                    disabled={paymentLoading}
                  >
                    {paymentLoading ? "⏳ Registrando..." : "✓ Registrar Pago"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedSubscription && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="modal-content delete-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header delete-modal-header">
              <h3>⚠️ Confirmar Eliminación</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="close-button"
                disabled={deleteLoading}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-text">
                ¿Estás seguro de que quieres eliminar permanentemente esta
                suscripción?
              </p>

              <div className="delete-warning-box">
                <p className="delete-warning-title">
                  Esta acción eliminará permanentemente:
                </p>
                <ul className="delete-warning-list">
                  <li>
                    Negocio:{" "}
                    <strong>{selectedSubscription.businessName}</strong>
                  </li>
                  <li>
                    Contacto:{" "}
                    <strong>{selectedSubscription.contactName}</strong>
                  </li>
                  <li>
                    Correo: <strong>{selectedSubscription.email}</strong>
                  </li>
                  <li>Agente de Voz ElevenLabs (si fue creado)</li>
                  <li>
                    Todos los archivos cargados (
                    {selectedSubscription.uploadedFiles?.length || 0} archivos)
                  </li>
                  <li>Todos los datos e historial de suscripción</li>
                </ul>
              </div>

              <div className="delete-danger-box">
                <p className="delete-danger-text">
                  ⚠️ ¡Esta acción NO se puede deshacer!
                </p>
              </div>

              <div className="delete-actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="delete-cancel-button"
                  disabled={deleteLoading}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteSubscription}
                  className="delete-confirm-button"
                  disabled={deleteLoading}
                >
                  {deleteLoading
                    ? "Eliminando..."
                    : "🗑️ Eliminar Permanentemente"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer Credits */}
      <div className="admin-footer-credits">
        <p>
          Sistema desarrollado por{" "}
          <span className="developer-name">Rafael Martinez</span> • © 2025
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
